// Renders an Ask-mode answer: light markdown (paragraphs, bullets, h3
// headings, **bold**) with inline citation pills parsed from the text.

import type { ReactNode } from 'react';
import Citation from './Citation';
import type { CitationRef } from './Citation';
import './AnswerAsk.css';

// Inline citation markers: [PL p.42] and [PN: Ekonomija, p.8]. Tolerant of
// page ranges ([PL p.87-91]) — the first page is used.
const INLINE_CITATION_RE =
  /\[PL\s*p\.?\s*(\d+)(?:\s*[-–]\s*\d+)?\]|\[PN:\s*([^,\]]+?),\s*p\.?\s*(\d+)(?:\s*[-–]\s*\d+)?\]/g;

function renderBold(text: string, keyBase: string): ReactNode[] {
  // split() with a capturing group: odd indices are the bold captures.
  return text.split(/\*\*(.+?)\*\*/g).map((part, i) =>
    i % 2 === 1 ? <strong key={`${keyBase}-b${i}`}>{part}</strong> : part,
  );
}

/** Render a run of inline text — **bold** + citation pills. Exported so
 *  AnswerCompare can reuse it for summaries / specifics. */
export function renderInline(
  text: string,
  onOpenCitation: (c: CitationRef) => void,
  keyBase: string,
): ReactNode[] {
  const nodes: ReactNode[] = [];
  let last = 0;
  let i = 0;
  INLINE_CITATION_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = INLINE_CITATION_RE.exec(text)) !== null) {
    if (m.index > last) {
      nodes.push(...renderBold(text.slice(last, m.index), `${keyBase}-t${i}`));
    }
    const cite: CitationRef = m[1]
      ? { party: 'PL', page: parseInt(m[1], 10), chapter: null }
      : { party: 'PN', page: parseInt(m[3], 10), chapter: m[2].trim() };
    nodes.push(
      <Citation key={`${keyBase}-c${i}`} cite={cite} onOpen={onOpenCitation} />,
    );
    last = INLINE_CITATION_RE.lastIndex;
    i++;
  }
  if (last < text.length) {
    nodes.push(...renderBold(text.slice(last), `${keyBase}-tend`));
  }
  return nodes;
}

function renderMarkdown(
  text: string,
  onOpenCitation: (c: CitationRef) => void,
): ReactNode[] {
  const blocks: ReactNode[] = [];
  let para: string[] = [];
  let bullets: string[] = [];
  let k = 0;

  const flushPara = () => {
    if (para.length) {
      const key = `p${k++}`;
      blocks.push(<p key={key}>{renderInline(para.join(' '), onOpenCitation, key)}</p>);
      para = [];
    }
  };
  const flushBullets = () => {
    if (bullets.length) {
      const key = `u${k++}`;
      blocks.push(
        <ul key={key}>
          {bullets.map((b, bi) => (
            <li key={`${key}-${bi}`}>
              {renderInline(b, onOpenCitation, `${key}-${bi}`)}
            </li>
          ))}
        </ul>,
      );
      bullets = [];
    }
  };

  for (const raw of text.split('\n')) {
    const line = raw.trim();
    if (!line) {
      flushPara();
      flushBullets();
      continue;
    }
    const heading = line.match(/^#{1,6}\s+(.*)$/);
    if (heading) {
      flushPara();
      flushBullets();
      const key = `h${k++}`;
      blocks.push(
        <h3 key={key}>{renderInline(heading[1], onOpenCitation, key)}</h3>,
      );
      continue;
    }
    const bullet = line.match(/^[-*]\s+(.*)$/);
    if (bullet) {
      flushPara();
      bullets.push(bullet[1]);
      continue;
    }
    flushBullets();
    para.push(line);
  }
  flushPara();
  flushBullets();
  return blocks;
}

export default function AnswerAsk({
  text,
  onOpenCitation,
}: {
  text: string;
  onOpenCitation: (c: CitationRef) => void;
}) {
  return <div className="mf-ask">{renderMarkdown(text, onOpenCitation)}</div>;
}
